import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import MultipleAttrDropdown from 'commons/src/components/dropdown/MultipleAttrDropdown';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import ModalWrapper from 'commons/src/components/modals/ModalWrapper';
import ResponseBox from 'commons/src/components/responseMessages/ResponseBox';
import SensorIcon from 'commons/src/components/sensors/SensorIcon';
import ThresholdSetting from 'commons/src/components/thresholds/ThresholdSetting';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { SelectedPeriod } from 'commons/src/models/commonTypeScript';
import { SpaceThresholds } from 'commons/src/models/customThresholdModels';
import styles from './SpaceThresholdsModal.module.scss';
import useSpaceThresholdsModal from './useSpaceThresholdsModal';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    sensorType: SensorTypes;
    thresholds: SpaceThresholds;
    locationId: string;
    spaceId: string;
    selectedPeriod: SelectedPeriod;
};

const SpaceThresholdsModalComponent = ({
    onClose,
    sensorType,
    thresholds,
    locationId,
    spaceId,
    selectedPeriod,
    isOpen,
}: Props): ReactElement => {
    const { t: txt } = useTranslation();
    const {
        thresholdScopeOptions,
        selectedValues,
        loading,
        error,
        handleThresholdScopeChange,
        selectedThresholdScope,
        onSave,
        setSelectedValues,
        getButtonText,
        scopeModified,
    } = useSpaceThresholdsModal({ onClose, sensorType, thresholds, locationId, spaceId, selectedPeriod });

    const customHeader: ReactElement = (
        <div className={styles.header}>
            <div className={styles.headerSensor}>
                <SensorIcon sensor={sensorType} />
                <div>{txt('Space.SensorThresholds', { sensor: txt(sensorType) })}</div>
            </div>
            <TertiaryButton onClick={onClose} noColor icon={<MaterialIcon name="close" data-testid="close-button" />} />
        </div>
    );
    const thresholdScope = selectedThresholdScope?.scope || ThresholdsResourceScope.USER_GROUP;
    const thresholdOptions = thresholds?.thresholdOptions?.options?.[thresholdScope] || [];

    return (
        <ModalWrapper isOpen={isOpen} onClose={onClose} customHeader={customHeader} size="small">
            <div className={styles.body}>
                <div className={styles.description}>{txt(`CustomThresholds.${sensorType}Description`)}</div>
                <div className={styles.fullWidth}>
                    <MultipleAttrDropdown
                        id="thresoldScopeSelector"
                        options={thresholdScopeOptions}
                        onSelect={handleThresholdScopeChange}
                        value={selectedThresholdScope?.inputValue}
                        slim
                        title=""
                        customHeader={<div className={styles.customHeader}>{txt('Spaces.ThresholdSettings')}</div>}
                        defaultOption="Select"
                        testAttr="threshold-scope-dropdown"
                        data-testid="threshold-scope-dropdown"
                    />
                </div>
                <ThresholdSetting
                    sensorType={sensorType}
                    customOptions={thresholds.customOptions}
                    ranges={thresholds.ranges}
                    thresholdOptions={thresholdOptions}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    onSave={onSave}
                    buttonText={getButtonText()}
                    scopeModified={scopeModified}
                    loading={loading}
                />
                {error && (
                    <div className={styles.errorWrapper}>
                        <ResponseBox text={`ErrorCodes.${error.error}`} />
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

export default SpaceThresholdsModalComponent;
